<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue';

import type { IconName } from '@/ui-kit/components/icon';
import type { ValidationRule } from '@/ui-kit/type/validation';

type ModelValue = string | number | null;

interface Props {
  modelValue?: ModelValue;
  label?: string;
  placeholder?: string;
  rules?: ValidationRule[];
  mask?: string;
  hint?: string;
  loading?: boolean;
  disable?: boolean;
  readonly?: boolean;
  outlined?: boolean;
  lazyRules?: boolean;
  inputmode?: 'search' | 'text' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | undefined;
  icon?: IconName;
  iconAfter?: boolean;
  noErrorIcon?: boolean;
  hideBottomSpace?: boolean;
  bottomSlots?: boolean;
  clearable?: boolean;
  textError?: string;
  error?: boolean;
  maxlength?: number;
}

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: '',
  placeholder: '',
  rules: () => [],
  mask: undefined,
  hint: '',
  loading: false,
  disable: false,
  readonly: false,
  outlined: true,
  lazyRules: false,
  inputmode: 'text',
  iconAfter: false,
  icon: undefined,
  noErrorIcon: false,
  hideBottomSpace: true,
  bottomSlots: false,
  clearable: false,
  textError: '',
  error: false,
  maxlength: undefined,
});

const emit = defineEmits<{ (event: 'update:modelValue', value: ModelValue): void }>();
const attrs = useAttrs();
const inputRef = ref<HTMLInputElement | null>(null);

const isDirty = ref(false);
const errorText = ref('');
const showError = computed(() => !!errorText.value && (isDirty.value || !props.lazyRules));
const displayError = computed(() => showError.value || !!props.textError || props.error);
const resolvedErrorText = computed(() => props.textError || errorText.value);
const isFocused = ref(false);
const hasValue = computed(() => {
  const value = props.modelValue;
  return value !== null && value !== undefined && String(value).length > 0;
});

const applyMask = (value: string) => {
  if (!props.mask) return value;
  const onlyDigits = value.replace(/\D/g, '');
  const max = props.mask.split('').filter((ch) => ch === '#').length;
  return onlyDigits.slice(0, max);
};

const validate = (value: unknown) => {
  for (const rule of props.rules || []) {
    // TODO: Нужно подумать как поправить
    // @ts-ignore
    const result = rule(value);
    if (result !== true) {
      errorText.value = typeof result === 'string' ? result : '';
      return false;
    }
  }
  errorText.value = '';
  return true;
};

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const nextValue = applyMask(target.value);
  if (nextValue !== target.value) target.value = nextValue;
  emit('update:modelValue', nextValue);
  if (!props.lazyRules) validate(nextValue);
};

const onBlur = () => {
  isFocused.value = false;
  isDirty.value = true;
  validate(props.modelValue);
};

const onFocus = (event: FocusEvent) => {
  if (props.readonly) {
    (event.target as HTMLInputElement).blur();
    return;
  }
  isFocused.value = true;
};

const clearValue = () => {
  if (props.readonly) return;
  emit('update:modelValue', '');
  if (!props.lazyRules) validate('');
};

const validateCurrent = () => validate(props.modelValue);
const focusCurrent = () => inputRef.value?.focus();

defineExpose({ validate: validateCurrent, focus: focusCurrent });

watch(
  () => props.modelValue,
  (value) => {
    if (!props.lazyRules || isDirty.value) validate(value);
  }
);
</script>

<template>
  <div
    class="ui-input"
    :class="[
      attrs.class,
      {
        'ui-input--outlined': outlined,
        'ui-input--disabled': disable,
        'ui-input--readonly': readonly,
        'ui-input--focused': isFocused,
        'ui-input--filled': hasValue,
        'ui-input--error': displayError,
        'ui-input--no-label': !label,
      },
    ]"
  >
    <div class="ui-input__control">
      <div v-if="label" class="ui-input__label">{{ label }}</div>
      <slot name="before" />
      <slot name="prepend" />
      <input
        ref="inputRef"
        class="ui-input__field"
        :value="modelValue ?? ''"
        :placeholder="placeholder"
        :disabled="disable || loading"
        :readonly="readonly"
        :inputmode="inputmode"
        :maxlength="maxlength"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <div
        v-if="clearable && !disable && !loading && !readonly && (modelValue ?? '') !== ''"
        class="ui-input__clear"
        @click="clearValue"
      >
        <ui-icon name="remove" />
      </div>
      <slot name="append" />
      <slot name="after" />
    </div>
    <div v-if="!hideBottomSpace || displayError || hint || $slots.hint || $slots.error" class="ui-input__bottom">
      <div class="ui-input__messages">
        <div v-if="displayError" class="ui-input__error">
          <slot name="error">{{ resolvedErrorText }}</slot>
        </div>
        <div v-else-if="$slots.hint" class="ui-input__hint">
          <slot name="hint" />
        </div>
        <div v-else-if="hint" class="ui-input__hint">{{ hint }}</div>
      </div>
      <slot v-if="bottomSlots" name="bottom" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-input {
  display: block;
  width: 100%;
  color: $black;

  &__label {
    font-size: 16px;
    line-height: 1.25;
    font-weight: 400;
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: $tree;
    pointer-events: none;
    background: transparent;
    padding: 0 4px;
    transition:
      transform 0.22s cubic-bezier(0.2, 0.6, 0.2, 1),
      font-size 0.22s cubic-bezier(0.2, 0.6, 0.2, 1),
      color 0.22s cubic-bezier(0.2, 0.6, 0.2, 1),
      top 0.22s cubic-bezier(0.2, 0.6, 0.2, 1),
      background-color 0.22s cubic-bezier(0.2, 0.6, 0.2, 1);
  }

  &__control {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 56px;
    padding: 0 10px;
    border-radius: 8px;
    background: $white;
  }

  &__field {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    line-height: 24px;
    padding-top: 24px;
    padding-bottom: 8px;
  }

  &__bottom {
    padding: 8px 0 0 2px;
    font-size: 0.75rem;
    min-height: 20px;
    line-height: 1;
  }

  &__hint {
    font-size: 0.75rem;
    color: $tree;
  }

  &__error {
    font-size: 0.75rem;
    color: $warning;
  }

  &--outlined &__control {
    border: 1px solid $five;
  }

  &--outlined:focus-within &__control {
    border-color: $black;
  }

  &--error &__control {
    border-color: $warning;
  }

  &--error:focus-within &__control {
    border-color: $warning;
  }

  &--readonly:focus-within &__control {
    border-color: $five;
  }

  &--focused &__label,
  &--filled &__label {
    top: 10px;
    transform: translateY(0);
    font-size: 0.75rem;
    background: $white;
  }

  &--no-label &__field {
    padding-top: 0;
    padding-bottom: 0;
  }

  &--no-label &__control {
    align-items: center;
  }

  &--filled &__label {
    color: $tree;
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--readonly {
    cursor: default;
  }

  &__clear {
    border: none;
    background: transparent;
    color: $six;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }

  &__clear:hover {
    color: $black;
  }
}
</style>
